import { MenuService } from '@/layout/service/menu.service';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { PessoaService } from '@/shared/services/pessoa.service';
import { PrestadorDadosGeraisService } from '@/core/api/sicom-api/registro/prestador/prestador-dados-gerais.service';
import { CLASSIFICACAO_ADMINISTRADORA, createPrestadoresMenu } from '@/features/registro/prestadores/prestadores.constants';

/**
 * Componente principal (layout) para o módulo de Prestadores.
 * Responsável por renderizar as rotas filhas, gerenciar o menu lateral
 * e controlar a exibição do diálogo de pesquisa de pessoas.
 */
@Component({
    selector: 'app-prestadores-content',
    standalone: true,
    imports: [RouterModule, Pesquisa],
    template: `
        <!-- No seu componente de layout principal -->
        <main class="w-full">
            <div>
                <router-outlet></router-outlet>
            </div>
        </main>
        <app-pesquisa [(showDialog)]="pesquisarDialog" (pessoa)="carregarPessoa($event)"></app-pesquisa>
    `
})
export class PrestadoresContent implements OnInit, OnDestroy {
    /** Controla a visibilidade do diálogo de pesquisa. */
    pesquisarDialog: boolean = false;

    /** Caminho base para as rotas do módulo. */
    rootPath = 'prestadores';

    private pessoaSub?: Subscription;

    /**
     * Construtor para injetar serviços essenciais.
     * @param menuService - Serviço para gerenciar o estado do menu principal da aplicação.
     * @param router - Serviço do Angular para controle de navegação.
     */
    constructor(
        private menuService: MenuService,
        private router: Router,
        private pessoaService: PessoaService,
        private dadosGeraisService: PrestadorDadosGeraisService
    ) {}

    /**
     * Hook do ciclo de vida. Executado na inicialização do componente.
     * Define o menu específico deste módulo no serviço de menu global e
     * reconstrói o menu (com/sem "Geridas") sempre que o prestador muda.
     */
    ngOnInit(): void {
        this.limparPessoaDeOutroModulo();
        this.menuService.setMenu(createPrestadoresMenu({ pesquisarPessoa: () => (this.pesquisarDialog = true) }, false));
        this.menuService.setSistema('SICOM | Prestadores');

        this.atualizarMenuPorClassificacao(this.pessoaService.getPessoaReference());
        this.pessoaSub = this.pessoaService.pessoaAtual$.subscribe((pessoa) => {
            this.atualizarMenuPorClassificacao(pessoa && pessoa.id && pessoa.uf ? { id: Number(pessoa.id), uf: pessoa.uf } : null);
        });
    }

    /**
     * Descarta a pessoa em sessão se ela pertencer a outro módulo (ex.: um médico
     * selecionado em Médicos) — evita que o card de relatórios de prestador venha
     * pré-preenchido com a seleção de um módulo diferente.
     */
    private limparPessoaDeOutroModulo(): void {
        const referencia = this.pessoaService.getPessoaReference();
        if (referencia?.tipo && referencia.tipo !== 'PRESTADOR') {
            this.pessoaService.clearPessoa();
        }
    }

    ngOnDestroy(): void {
        this.pessoaSub?.unsubscribe();
    }

    /** Busca os dados gerais do prestador e reconstrói o menu conforme a classificação. */
    private atualizarMenuPorClassificacao(ref: { id: number; uf: string } | null): void {
        if (!ref?.id || !ref.uf) {
            this.menuService.setMenu(createPrestadoresMenu({ pesquisarPessoa: () => (this.pesquisarDialog = true) }, false));
            return;
        }

        this.dadosGeraisService.buscar({ id: ref.id, uf: ref.uf }).subscribe({
            next: (dados) => this.menuService.setMenu(createPrestadoresMenu({ pesquisarPessoa: () => (this.pesquisarDialog = true) }, dados?.idClassificacao === CLASSIFICACAO_ADMINISTRADORA)),
            error: () => this.menuService.setMenu(createPrestadoresMenu({ pesquisarPessoa: () => (this.pesquisarDialog = true) }, false))
        });
    }

    /**
     * Força o recarregamento do componente da rota especificada, mesmo que já esteja ativa.
     * Navega para uma rota temporária e depois retorna à original para forçar a reinicialização.
     * @param rota A rota de destino para recarregar.
     */
    private async executar(rota: string) {
        await this.router.navigateByUrl('/', { skipLocationChange: true });
        await this.router.navigate([rota]);
    }

    /**
     * Callback executado quando uma pessoa é selecionada no diálogo de pesquisa.
     * Salva os dados da pessoa na sessão e recarrega a rota principal do módulo.
     * @param pessoa O objeto da pessoa selecionada.
     */
    carregarPessoa(pessoa?: Pessoa) {
        if (pessoa) {
            this.pessoaService.setPessoa(pessoa);
            this.executar(this.rootPath);
        } else {
            console.error('Registro não encontrado!');
        }
    }
}
